import React from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { hasAccess } from '../utils/permissions';
import { PageShell, PageHeader, Surface, Row, RowText, RowMeta, StatusPill, ActionButton, StatePanel } from '../components/ui';

// Display order and human labels for the keys in KNOWN_FLAGS in skilzBackend
// app/services/featureFlags.js (the source of truth for keys). Descriptions are
// NOT kept here: GET /admin/feature-flags returns them, and the page renders the
// server's text.
export const FEATURE_FLAGS = [
  { key: 'selfServeAccountDeletion', label: 'Self-serve account deletion' },
  { key: 'accountDeletionSideEffects', label: 'Account deletion side-effects' },
  { key: 'accountDeactivation', label: 'Account deactivation' },
  { key: 'skillSoftDelete', label: 'Skill soft delete' },
  { key: 'skillDeactivation', label: 'Skill deactivation' },
  { key: 'disableSanctionedOwnerBookingGate', label: 'Disable sanctioned-owner booking gate' },
  { key: 'disableListingOwnerStatusNormalization', label: 'Disable listing owner-status normalization' },
  { key: 'counterpartySkillPageException', label: 'Counterparty skill page exception' },
  { key: 'localFeedSkillStatusFilter', label: 'Local feed skill-status filter' },
  { key: 'retentionSweepDryRun', label: 'Retention sweep (dry run)' },
  { key: 'shareSignupAttribution', label: 'Share signup attribution' },
  { key: 'shareStoryCards', label: 'Share story cards' },
  { key: 'uploadImageFormatIntegrity', label: 'Upload image format integrity' },
  { key: 'videoPosting', label: 'Short video posting (pilot)' },
  { key: 'videoPostingNonAdmins', label: 'Let ordinary users post video (not just admins)' },
  { key: 'videoBrandingRender', label: 'Branded video rendering (Option 1 intro/outro)' },
];

// Flags whose OFF state is NOT the safe one. For most flags OFF is the inert,
// pre-feature behaviour; for these, OFF actively runs a riskier legacy path.
// Keyed by flag key; the value is shown on the row and in the turn-off prompt.
export const DANGEROUS_WHEN_OFF = {
  skillSoftDelete: 'OFF is NOT safe for this flag: OFF runs the legacy HARD DELETE, which permanently deletes every request attached to a deleted skill, including clients\' completed (DONE) bookings. ON is the safer behaviour (blocked soft delete that keeps every request).',
};

// Backend propagation: flag values are memoized per process for a short TTL.
const PROPAGATION_SECONDS = 30;

// Backend error payload shape: { errors: { msg: 'CODE' } }. Surface the code,
// falling back to the HTTP error message, never a generic string.
const errorText = err => (err && err.body && err.body.errors && err.body.errors.msg)
  || (err && err.message)
  || (err && err.status ? `HTTP ${err.status}` : 'UNKNOWN_ERROR');

const onOff = value => (value ? 'On' : 'Off');

const formatDate = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

export default function FeatureFlags() {
  const { permissions, loaded } = usePermissions();
  const roles = (permissions && permissions.adminRoles) || [];
  const rolesKey = roles.slice().sort().join(',');
  // GET and PATCH /admin/feature-flags are both SUPER_ADMIN-only on the backend.
  const canView = hasAccess('feature-flags', roles);
  // Write access is SUPER_ADMIN only. Deliberately not hasAccess(), so a future
  // widening of read access in PERMISSION_MAP cannot also grant write.
  const canWrite = roles.includes('SUPER_ADMIN');

  const [flags, setFlags] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [busy, setBusy] = React.useState({});
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const requestSeq = React.useRef(0);

  const loadFlags = React.useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true); setLoadError('');
    try {
      const { json } = await httpClient(`${API_BASE_URL}/admin/feature-flags`);
      // Unlike /admin/meta-flags, `flags` is an ARRAY of flag objects.
      const list = json && Array.isArray(json.flags) ? json.flags : [];
      if (seq === requestSeq.current) setFlags(list.filter(flag => flag && typeof flag.key === 'string'));
    } catch (err) {
      if (seq === requestSeq.current) setLoadError(errorText(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => { if (loaded && canView) loadFlags(); }, [loaded, rolesKey, loadFlags]);

  const toggle = async flag => {
    if (!canWrite || busy[flag.key] || !flags) return;
    const next = flag.enabled !== true;
    const danger = !next && DANGEROUS_WHEN_OFF[flag.key];
    const prompt = next
      ? `Turn ON "${flag.label}" (${flag.key})?\n\nThis changes production behaviour immediately for all users (allow up to ~${PROPAGATION_SECONDS}s to reach every server).`
      : `Turn OFF "${flag.label}" (${flag.key})?\n\nThis kill switch changes production behaviour immediately (allow up to ~${PROPAGATION_SECONDS}s to reach every server).`
        + (danger ? `\n\nWARNING: ${danger}` : '');
    if (!window.confirm(prompt)) return;
    setBusy(current => ({ ...current, [flag.key]: true }));
    setError(''); setMessage('');
    try {
      await httpClient(`${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(flag.key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: next }),
      });
      setMessage(`${flag.label} turned ${next ? 'ON' : 'OFF'}. Allow up to ~${PROPAGATION_SECONDS}s to take effect everywhere.`);
      // Show server truth, not our assumption of it.
      await loadFlags();
    } catch (err) {
      setError(`Could not update ${flag.label}: ${errorText(err)}`);
    } finally {
      setBusy(current => { const copy = { ...current }; delete copy[flag.key]; return copy; });
    }
  };

  if (!loaded) return <PageShell><StatePanel state="loading">Loading permissions…</StatePanel></PageShell>;
  if (!canView) return <PageShell><StatePanel state="error">You do not have permission to view this page.</StatePanel></PageShell>;

  // Render the server's flags, not just the ones known at build time: a flag
  // added in the backend must never be invisible/untoggleable here. Known keys
  // keep their curated order and labels; unknown keys are appended (sorted)
  // with their raw key as the label. Descriptions always come from the server.
  const rank = key => {
    const index = FEATURE_FLAGS.findIndex(flag => flag.key === key);
    return index === -1 ? FEATURE_FLAGS.length : index;
  };
  const rows = flags
    ? flags
      .map(flag => {
        const known = FEATURE_FLAGS.find(entry => entry.key === flag.key);
        return { ...flag, label: known ? known.label : flag.key };
      })
      .sort((a, b) => (rank(a.key) - rank(b.key)) || a.key.localeCompare(b.key))
    : [];

  return <PageShell>
    <PageHeader eyebrow="System" title="Feature Flags" description="Backend kill switches. For most flags Off is the safe, pre-feature behaviour; any flag where Off is dangerous is marked on its row." />
    <StatePanel state="empty">Changes can take up to ~{PROPAGATION_SECONDS} seconds to take effect everywhere: each server caches flag values briefly. Every change is written to the admin audit log.</StatePanel>
    {!canWrite && <StatePanel state="empty">Read-only: only a Super Admin can change these flags.</StatePanel>}
    {error && <StatePanel state="error">{error}</StatePanel>}
    {message && <StatePanel state="empty">{message}</StatePanel>}
    <Surface title="Kill switches" actions={<ActionButton variant="secondary" type="button" onClick={loadFlags} disabled={loading}>Refresh</ActionButton>}>
      {loadError && <StatePanel state="error">Unable to load feature flags: {loadError}</StatePanel>}
      {loading && !flags && <StatePanel state="loading">Loading feature flags…</StatePanel>}
      {flags && !rows.length && !loadError && <StatePanel state="empty">The server returned no feature flags.</StatePanel>}
      {flags && rows.map(flag => {
        const on = flag.enabled === true;
        const builtInDefault = flag.default === true;
        const drifted = on !== builtInDefault;
        const rowBusy = Boolean(busy[flag.key]);
        const danger = DANGEROUS_WHEN_OFF[flag.key];
        return <Row key={flag.key} right={<>
          {rowBusy && <span className="sk-muted" role="status">Saving…</span>}
          <ActionButton
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={flag.label}
            variant={on ? 'primary' : 'secondary'}
            disabled={!canWrite || rowBusy}
            aria-busy={rowBusy || undefined}
            onClick={() => toggle(flag)}
          >{onOff(on)}</ActionButton>
        </>}>
          <RowText>
            <strong className="sk-title">{flag.label}</strong> <StatusPill status={on ? 'active' : 'deactivated'}>{onOff(on)}</StatusPill>
            <RowMeta>{flag.description || 'No description returned by the server.'}</RowMeta>
            <RowMeta dim><code>{flag.key}</code> · Default: {onOff(builtInDefault)}</RowMeta>
            {drifted && <RowMeta><span style={{ color: 'var(--sk-warning)', fontWeight: 700 }} data-testid="drift-indicator">Changed from default: live value is {onOff(on)}, built-in default is {onOff(builtInDefault)}.</span></RowMeta>}
            {(flag.updatedAt || flag.updatedBy) && <RowMeta dim><span data-testid="last-changed">Last changed{flag.updatedAt ? ` ${formatDate(flag.updatedAt)}` : ''}{flag.updatedBy ? ` by ${flag.updatedBy}` : ''}</span></RowMeta>}
            {danger && <p style={{ color: 'var(--sk-danger)', fontWeight: 700, margin: '6px 0 0' }} role="alert" data-testid="dangerous-off-warning">{danger}{!on ? ' This flag is currently OFF.' : ''}</p>}
          </RowText>
        </Row>;
      })}
    </Surface>
  </PageShell>;
}
